import React, { useMemo, useState, useEffect } from "react";
import { FileDown, ChevronDown, ChevronUp, Star } from "lucide-react";

type Hukdis = "none" | "ringan" | "sedang";

interface Employee {
  id: string;
  nama: string;
  pangkatGol: string;
  nip: string;
  npwp: string;
  statusPegawai: string;
  statusWP: string;
  uraianKelasJabatan: string;
  nilaiKelasJabatan: string;
  tunjanganJabatan: number; // Pemisahan komponen tunjangan jabatan[cite: 1]
  tunjanganStatus: number;  // Pemisahan komponen tunjangan status[cite: 1]
  // Pemotongan karena terlambat (jumlah hari) — TL.1 s/d TL.5
  tl: [number, number, number, number, number];
  // Pemotongan karena pulang sebelum waktunya (jumlah hari) — PSW.1 s/d PSW.5
  psw: [number, number, number, number, number];
  lupaHadir: number; // lupa absen masuk (hari)
  lupaPulang: number; // lupa absen pulang (hari)
  izin: number; // hari
  tanpaKet: number; // tidak masuk tanpa keterangan (hari)
  hukdis: Hukdis; // hukuman disiplin
  tubel: boolean; // tugas belajar/diklat > 6 bulan
}

/* ---------- Konstanta tarif (sesuai lembar RUMUS PERHITUNGAN) ---------- */

const TL_RATES = [0.0025, 0.005, 0.0075, 0.01, 0.025]; // TL.1 - TL.5
const PSW_RATES = [0.0025, 0.005, 0.0075, 0.01, 0.025]; // PSW.1 - PSW.5
const LUPA_HADIR_RATE = 0.025;
const LUPA_PULANG_RATE = 0.025;
const IZIN_RATE = 0.025;
const TANPA_KET_RATE = 0.05;
const HUKDIS_RINGAN = 15; // poin %, flat
const HUKDIS_SEDANG = 25; // poin %, flat
const TUBEL_RATE = 10; // poin %, flat
const TK_MULTIPLIER = 0.6; // 60% x Basis Tunjangan

const TL_LABELS = ["TL.1", "TL.2", "TL.3", "TL.4", "TL.5"];
const PSW_LABELS = ["PSW.1", "PSW.2", "PSW.3", "PSW.4", "PSW.5"];

/* ---------- Util ---------- */

const rupiah = (n: number) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Math.round(n) || 0);

const pct = (n: number) => `${(Math.round(n * 100) / 100).toFixed(2)}%`;

const uid = () => Math.random().toString(36).slice(2, 10);

/* ---------- Perhitungan ---------- */

interface Breakdown {
  tlPct: number[];
  pswPct: number[];
  lupaHadirPct: number;
  lupaPulangPct: number;
  izinPct: number;
  tanpaKetPct: number;
  hukdisPct: number;
  tubelPct: number;
  totalPct: number;
  totalRp: number;
  totalTunjangan: number; // Total gabungan tunjangan jabatan & status
}

function computeBreakdown(e: Employee): Breakdown {
  const tlPct = e.tl.map((h, i) => h * TL_RATES[i] * 100);
  const pswPct = e.psw.map((h, i) => h * PSW_RATES[i] * 100);
  const lupaHadirPct = e.lupaHadir * LUPA_HADIR_RATE * 100;
  const lupaPulangPct = e.lupaPulang * LUPA_PULANG_RATE * 100;
  const izinPct = e.izin * IZIN_RATE * 100;
  const tanpaKetPct = e.tanpaKet * TANPA_KET_RATE * 100;
  const hukdisPct = e.hukdis === "ringan" ? HUKDIS_RINGAN : e.hukdis === "sedang" ? HUKDIS_SEDANG : 0;
  const tubelPct = e.tubel ? TUBEL_RATE : 0;

  const totalPct =
    tlPct.reduce((a, b) => a + b, 0) +
    pswPct.reduce((a, b) => a + b, 0) +
    lupaHadirPct +
    lupaPulangPct +
    izinPct +
    tanpaKetPct +
    hukdisPct +
    tubelPct;

  const totalTunjangan = (e.tunjanganJabatan || 0) + (e.tunjanganStatus || 0);
  const totalRp = (totalPct / 100) * TK_MULTIPLIER * totalTunjangan;

  return {
    tlPct,
    pswPct,
    lupaHadirPct,
    lupaPulangPct,
    izinPct,
    tanpaKetPct,
    hukdisPct,
    tubelPct,
    totalPct,
    totalRp,
    totalTunjangan,
  };
}

/* ---------- Header sel grup ---------- */

function GroupTh({
  children,
  colSpan,
  tone = "default",
}: {
  children: React.ReactNode;
  colSpan: number;
  tone?: "default" | "amber" | "navy";
}) {
  const toneClass =
    tone === "amber"
      ? "bg-[#3A2F1B] text-[#E7C878]"
      : tone === "navy"
      ? "bg-[#0F1E38] text-[#9FB3D1]"
      : "bg-[#152544] text-white";
  return (
    <th
      colSpan={colSpan}
      className={`${toneClass} sticky top-0 z-20 border border-[#0B1830]/60 px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap`}
    >
      {children}
    </th>
  );
}

function SubTh({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="sticky z-20 border border-[#0B1830]/60 bg-[#1B2E52] px-1.5 py-1 text-center text-[10px] font-medium text-[#C9D6EC] whitespace-nowrap"
      style={{ top: 30 }}
    >
      {children}
    </th>
  );
}

/* ============================================================================
   Komponen utama
   ============================================================================ */

export default function RekapAbsensi() {
  const [instansi, setInstansi] = useState("Sekretariat Daerah Kabupaten/Kota");
  const [periode, setPeriode] = useState(() => {
    const tanggal = new Date();
    const tahun = tanggal.getFullYear();
    const bulan = String(tanggal.getMonth() + 1).padStart(2, "0");
    const hari = String(tanggal.getDate()).padStart(2, "0");
    return `${tahun}-${bulan}-${hari}`;
  });
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDetail, setShowDetail] = useState(true);

  // Fetch data dari API saat komponen dimuat atau periode/tanggal berubah
  useEffect(() => {
    const requestOptions = {
      method: "GET",
      redirect: "follow" as RequestRedirect,
    };

    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/bawaslu_phwt/rekapan/absensi?tanggal=${periode}`, requestOptions)
      .then((response) => response.json()) // Sesuaikan ke .json() agar langsung berupa objek/array
      .then((result) => {
        // Asumsikan struktur data dari API mengembalikan array data pegawai
        // Sesuaikan mapping property jika nama key dari API berbeda (misal: result.data)
        const rawData = Array.isArray(result) ? result : result.data || [];
        
        const mappedData: Employee[] = rawData.map((item: any) => ({
          id: item.id || uid(),
          nama: item.nama || "",
          pangkatGol: item.pangkatGol || "",
          nip: item.nip || "",
          npwp: item.npwp || "",
          statusPegawai: item.statusPegawai || "PNS",
          statusWP: item.statusWP || "TK/0",
          uraianKelasJabatan: item.uraianKelasJabatan || "",
          nilaiKelasJabatan: item.nilaiKelasJabatan || "",
          tunjanganJabatan: Number(item.tunjanganJabatan) || 0,
          tunjanganStatus: Number(item.tunjanganStatus) || 0,
          tl: item.tl || [0, 0, 0, 0, 0],
          psw: item.psw || [0, 0, 0, 0, 0],
          lupaHadir: Number(item.lupaHadir) || 0,
          lupaPulang: Number(item.lupaPulang) || 0,
          izin: Number(item.izin) || 0,
          tanpaKet: Number(item.tanpaKet) || 0,
          hukdis: item.hukdis || "none",
          tubel: Boolean(item.tubel),
        }));

        setEmployees(mappedData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Gagal memuat data absensi:", error);
        setLoading(false);
      });
  }, [periode]);

  const rows = useMemo(
    () => employees.map((e) => ({ e, b: computeBreakdown(e) })),
    [employees]
  );

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, { b }) => {
        acc.tk += b.totalTunjangan;
        acc.potongan += b.totalRp;
        return acc;
      },
      { tk: 0, potongan: 0 }
    );
  }, [rows]);

  const exportXlsx = async () => {
    const XLSX = await import("xlsx");
    const header1 = [
      "No.", "Nama", "Pangkat/Gol", "NIP", "NPWP", "Status Pegawai", "Status WP",
      "Nilai Kls Jab", "Tunjangan Jabatan", "Tunjangan Status", "Total Tunjangan",
      ...TL_LABELS.flatMap((l) => [`${l} Hr`, `${l} %`]),
      ...PSW_LABELS.flatMap((l) => [`${l} Hr`, `${l} %`]),
      "Lupa Hadir Hr", "Lupa Hadir %",
      "Lupa Pulang Hr", "Lupa Pulang %",
      "Izin Hr", "Izin %",
      "Tanpa Ket Hr", "Tanpa Ket %",
      "Hukuman Disiplin", "Hukdis %",
      "Tugas Belajar >6bln", "Tubel %",
      "Total %", "Total Pemotongan (Rp)",
    ];
    const data = rows.map(({ e, b }, i) => [
      i + 1, e.nama, e.pangkatGol, e.nip, e.npwp, e.statusPegawai, e.statusWP,
      e.uraianKelasJabatan, e.nilaiKelasJabatan, e.tunjanganJabatan, e.tunjanganStatus, b.totalTunjangan,
      ...e.tl.flatMap((h, idx) => [h, Number(b.tlPct[idx].toFixed(3))]),
      ...e.psw.flatMap((h, idx) => [h, Number(b.pswPct[idx].toFixed(3))]),
      e.lupaHadir, Number(b.lupaHadirPct.toFixed(3)),
      e.lupaPulang, Number(b.lupaPulangPct.toFixed(3)),
      e.izin, Number(b.izinPct.toFixed(3)),
      e.tanpaKet, Number(b.tanpaKetPct.toFixed(3)),
      e.hukdis === "none" ? "-" : e.hukdis, b.hukdisPct,
      e.tubel ? "Ya" : "-", b.tubelPct,
      Number(b.totalPct.toFixed(3)), Math.round(b.totalRp),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([
      [`Rekapitulasi Pemotongan Tunjangan Kinerja Bulan ${periode}`],
      [instansi],
      [],
      header1,
      ...data,
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rekap Absensi");
    XLSX.writeFile(wb, `Rekap_Absensi_${periode.replace(/\s+/g, "_")}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
{/* ---------- Kop / letterhead ---------- */}
      <div className="max-w-8xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center mb-4">
              <div className="flex ">
                <Star className="w-8 h-8 text-blue-600 mr-3" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Rekap Absensi</h1>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap gap-2">
              <input 
                type="date" 
                value={periode} 
                onChange={(e) => setPeriode(e.target.value)}
                className="border border-slate-300 rounded-xl px-3 py-1.5 text-xs bg-white"
              />
              <button
                onClick={() => setShowDetail((s) => !s)}
                className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
              >
                {showDetail ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {showDetail ? "Ringkas" : "Detail"}
              </button>
              <button
                onClick={exportXlsx}
                className="flex items-center gap-1.5 rounded-2xl bg-gradient-to-r from-yellow-400 to-amber-300 px-4 py-2 text-xs font-semibold text-slate-900 transition hover:from-yellow-300 hover:to-amber-200"
              >
                <FileDown size={14} />
                Ekspor Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Ringkasan ---------- */}
      {/* <div className="mx-auto mb-4 grid max-w-full grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Jumlah Pegawai", value: String(employees.length) },
          { label: "Total Tunjangan", value: rupiah(totals.tk) },
          { label: "Total Pemotongan", value: rupiah(totals.potongan) },
          { label: "Total Pencairan", value: rupiah(totals.tk - totals.potongan) },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-3xl border border-slate-200 bg-white/95 px-4 py-3 shadow-sm shadow-slate-200/40"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {s.label}
            </p>
            <p className="mt-1 truncate font-serif text-base font-semibold text-slate-900">
              {s.value}
            </p>
          </div>
        ))}
      </div> */}

      {/* ---------- Tabel ---------- */}
      <p className="mx-auto mb-1.5 max-w-full text-[11px] text-slate-500">
        Geser tabel ke kanan/kiri untuk melihat seluruh kolom pemotongan →
      </p>
      <div className="relative mx-auto max-w-full rounded-lg border border-[#0F1E38]/10 bg-white shadow-sm">
        <div className="rekap-scroll overflow-x-auto overflow-y-auto" style={{ maxHeight: "70vh" }}>
          <table className="w-max min-w-max border-collapse text-[12px]">
            <thead>
              <tr>
                <GroupTh colSpan={10} tone="navy">Data Pegawai &amp; Komponen Tunjangan</GroupTh>
                {showDetail && (
                  <>
                    <GroupTh colSpan={10}>Pemotongan Karena Terlambat</GroupTh>
                    <GroupTh colSpan={10}>Pemotongan Karena Pulang Sebelum Waktunya</GroupTh>
                    <GroupTh colSpan={4} tone="amber">Lupa Absen</GroupTh>
                    <GroupTh colSpan={8} tone="amber">Pemotongan Lain</GroupTh>
                  </>
                )}
                <GroupTh colSpan={1} tone="amber">Total Pemotongan</GroupTh>
              </tr>
              <tr>
                <SubTh>No.</SubTh>
                <SubTh>Nama</SubTh>
                <SubTh>Pangkat/Gol</SubTh>
                <SubTh>NIP</SubTh>
                <SubTh>NPWP</SubTh>
                <SubTh>Status Pegawai</SubTh>
                <SubTh>Status WP</SubTh>
                {/* <SubTh>Kelas Jabatan</SubTh> */}
                <SubTh>Nilai Kls</SubTh>
                <SubTh>Tunj. Jabatan</SubTh>
                <SubTh>Tunj. Status</SubTh>
                {/* <SubTh>Total Tunjangan</SubTh> */}

                {showDetail &&
                  TL_LABELS.map((l) => (
                    <React.Fragment key={l}>
                      <SubTh>{l} Hr</SubTh>
                      <SubTh>{l} %</SubTh>
                    </React.Fragment>
                  ))}
                {showDetail &&
                  PSW_LABELS.map((l) => (
                    <React.Fragment key={l}>
                      <SubTh>{l} Hr</SubTh>
                      <SubTh>{l} %</SubTh>
                    </React.Fragment>
                  ))}
                {showDetail && (
                  <>
                    <SubTh>Lupa Msk Hr</SubTh>
                    <SubTh>Lupa Msk %</SubTh>
                    <SubTh>Lupa Plg Hr</SubTh>
                    <SubTh>Lupa Plg %</SubTh>
                    <SubTh>Izin Hr</SubTh>
                    <SubTh>Izin %</SubTh>
                    <SubTh>Tnp Ket Hr</SubTh>
                    <SubTh>Tnp Ket %</SubTh>
                    <SubTh>Hukdis</SubTh>
                    <SubTh>Hukdis %</SubTh>
                    <SubTh>Tubel &gt;6bl</SubTh>
                    <SubTh>Tubel %</SubTh>
                  </>
                )}
                <SubTh>Total %</SubTh>
                {/* <SubTh>Total Rp</SubTh> */}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={35} className="py-8 text-center text-slate-500 italic">
                    Memuat data rekap absensi dari server...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={35} className="py-8 text-center text-slate-500 italic">
                    Tidak ada data pegawai yang tersedia.
                  </td>
                </tr>
              ) : (
                rows.map(({ e, b }, i) => (
                  <tr
                    key={e.id}
                    className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}
                  >
                    <td className="sticky left-0 z-10 border border-slate-200 bg-inherit px-2 py-1 text-center font-medium">
                      {i + 1}
                    </td>
                    <td className="sticky left-[2.25rem] z-10 border border-slate-200 bg-inherit px-2 py-1 font-medium whitespace-nowrap">
                      {e.nama || "—"}
                    </td>
                    <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">{e.pangkatGol || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">{e.nip || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">{e.npwp || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">{e.statusPegawai || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">{e.statusWP || "—"}</td>
                    {/* <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">{e.uraianKelasJabatan || "—"}</td> */}
                    <td className="border border-slate-200 px-2 py-1 text-center">{e.nilaiKelasJabatan || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1 text-right tabular-nums">{rupiah(e.tunjanganJabatan)}</td>
                    <td className="border border-slate-200 px-2 py-1 text-right tabular-nums">{rupiah(e.tunjanganStatus)}</td>
                    {/* <td className="border border-slate-200 px-2 py-1 text-right font-semibold tabular-nums text-[#0F1E38]">{rupiah(b.totalTunjangan)}</td> */}

                    {showDetail &&
                      e.tl.map((h, idx) => (
                        <React.Fragment key={idx}>
                          <td className="border border-slate-200 px-1.5 py-1 text-center tabular-nums">{h}</td>
                          <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">
                            {pct(b.tlPct[idx])}
                          </td>
                        </React.Fragment>
                      ))}
                    {showDetail &&
                      e.psw.map((h, idx) => (
                        <React.Fragment key={idx}>
                          <td className="border border-slate-200 px-1.5 py-1 text-center tabular-nums">{h}</td>
                          <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">
                            {pct(b.pswPct[idx])}
                          </td>
                        </React.Fragment>
                      ))}

                    {showDetail && (
                      <>
                        <td className="border border-slate-200 px-1.5 py-1 text-center tabular-nums">{e.lupaHadir}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">{pct(b.lupaHadirPct)}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-center tabular-nums">{e.lupaPulang}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">{pct(b.lupaPulangPct)}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-center tabular-nums">{e.izin}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">{pct(b.izinPct)}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-center tabular-nums">{e.tanpaKet}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">{pct(b.tanpaKetPct)}</td>
                        <td className="border border-slate-200 px-2 py-1 whitespace-nowrap">
                          {e.hukdis === "none" ? "—" : e.hukdis === "ringan" ? "Ringan" : "Sedang"}
                        </td>
                        <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">{pct(b.hukdisPct)}</td>
                        <td className="border border-slate-200 px-2 py-1 text-center">{e.tubel ? "Ya" : "—"}</td>
                        <td className="border border-slate-200 px-1.5 py-1 text-right tabular-nums text-slate-500">{pct(b.tubelPct)}</td>
                      </>
                    )}

                    <td className="border border-slate-200 px-1.5 py-1 text-right font-semibold tabular-nums text-[#8C6D2F]">
                      {pct(b.totalPct)}
                    </td>
                    {/* <td className="border border-slate-200 px-1.5 py-1 text-right font-semibold tabular-nums text-[#B23A2A]">
                      {rupiah(b.totalRp)}
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* <p className="mx-auto mt-3 max-w-full font-serif text-[11px] italic text-slate-500">
        Rumus: Total Tunjangan = Tunjangan Jabatan + Tunjangan Status. Total Pemotongan (Rp) = Σ% Pengurangan × 60% × Total Tunjangan.
      </p> */}
    </div>
  );
}